/**
 * Typed Redux hooks.
 *
 * Always use these rather than the raw `useDispatch`/`useSelector`: the untyped
 * versions give `any` for state, which silently defeats every type check the store
 * would otherwise provide.
 */
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AccessContext } from "./access";
import type { AppDispatch, RootState } from "./store";
import { reconcileCompany } from "./uiSlice";

// Module-level so the selector results keep a stable reference for an anonymous
// session. A fresh [] each render would break every downstream useMemo.
const NO_PERMISSIONS: readonly string[] = [];
const NO_ROLES: readonly string[] = [];

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

/** The signed-in user, or null while unknown or anonymous. */
export function useCurrentUser() {
  return useAppSelector((state) => state.auth.user);
}

/**
 * Permission check for conditional UI.
 *
 * A UI-only convenience. The server enforces every permission in middleware, so
 * hiding a button is a courtesy to the user, never a security control — anyone can
 * call the endpoint directly, and the 403 is what actually stops them.
 */
export function usePermission(...required: string[]): boolean {
  const permissions = useAppSelector((state) => state.auth.user?.permissions);
  if (permissions === undefined) return false;
  return required.some((permission) => permissions.includes(permission));
}

export function useHasRole(...roles: string[]): boolean {
  const held = useAppSelector((state) => state.auth.user?.roles);
  if (held === undefined) return false;
  return roles.some((role) => held.includes(role));
}

/**
 * Whether this account has a personnel self-service surface — My Leave, My
 * Attendance, and its own leave balance.
 *
 * Deliberately NOT a permission question, and this is the one place in the app
 * that knows the difference. The super administrator holds every permission by
 * construction, including `attendance:read:self` and `leave:apply`, so no
 * predicate over `permissions` can distinguish it from a real employee. Nor does
 * "has an Employee row" help: the seeded admin has one on purpose so it is not
 * nameless in every list.
 *
 * So it reads a fact about the account instead. The server decides
 * (User.isServiceAccount) and the client obeys.
 */
export function useHasSelfService(): boolean {
  const user = useCurrentUser();
  if (user === null) return false;
  return !user.isServiceAccount;
}

/**
 * The signed-in person's own employee id, when they have one.
 *
 * Needed because "send no employeeId" does not mean "me" on any scoped endpoint —
 * it means "whatever my permissions allow", which for an administrator is the
 * whole company.
 */
export function useOwnEmployeeId(): string | null {
  const user = useCurrentUser();
  return user?.employeeId ?? null;
}

/**
 * The company every list query should be filtered to.
 *
 * `undefined` means "do not send a companyId at all" — either the user chose
 * "All companies" or they only have one, in which case the server already scopes
 * them and sending it would be noise. Feed it straight into a query hook.
 */
export function useCompanyFilter(): string | undefined {
  const companyId = useAppSelector((state) => state.ui.companyId);
  return companyId ?? undefined;
}

/**
 * True when the switcher is worth rendering at all.
 *
 * A single-company operator gets no chooser: it would be a dropdown with one
 * entry that can never change anything.
 */
export function useHasMultipleCompanies(): boolean {
  const user = useCurrentUser();
  if (user === null) return false;
  return user.hasAllCompanyAccess || user.companies.length > 1;
}

/**
 * Validates the persisted company choice against what this session allows.
 *
 * Called once by the shell rather than by the switcher, because the switcher is
 * not mounted on a phone and renders nothing for a single-company user — and
 * without this, a company whose access was revoked (or which was deleted) stays
 * in localStorage and every list page answers 403 with no obvious way out.
 */
export function useReconcileCompany(): void {
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const companies = user?.companies ?? [];
  const allowAll = user?.hasAllCompanyAccess ?? false;

  // A joined string rather than the array, whose identity changes every render.
  const availableKey = companies.map((company) => company.id).join(",");

  useEffect(() => {
    if (user === null) return;
    dispatch(
      reconcileCompany({
        available: availableKey === "" ? [] : availableKey.split(","),
        allowAll,
      }),
    );
  }, [dispatch, user, availableKey, allowAll]);
}

/**
 * Departments the signed-in person heads.
 *
 * Separate from permissions on purpose: heading a department is a fact about the
 * org chart, and it grants sight of that department's attendance without any
 * `attendance:read` grant.
 */
export function useDepartmentsHeaded(): Array<{ id: string; name: string }> {
  const user = useCurrentUser();
  return user?.departmentsHeaded ?? [];
}

export function useIsDepartmentHead(): boolean {
  return useDepartmentsHeaded().length > 0;
}

/**
 * Whether the signed-in person manages anybody.
 *
 * Separate from permissions for the same reason as headship: `wfh:manage:team`
 * says what someone MAY do, this says whether there is anyone to do it for. A
 * manager-shaped role with no reports should not be pointed at an empty queue.
 */
export function useHasDirectReports(): boolean {
  const user = useCurrentUser();
  return (user?.directReportCount ?? 0) > 0;
}

/**
 * Everything a visibility gate is evaluated against, as one memoized object.
 *
 * Assembled here rather than at each call site so the sidebar, the quick
 * switcher and the documentation all answer from an identical view of the
 * session. The memo is what makes it safe to feed into a `useMemo` that filters
 * ~58 articles or builds a search index — without it, every render of the shell
 * header would recompute both.
 */
export function useAccessContext(): AccessContext {
  const permissions = useAppSelector((state) => state.auth.user?.permissions);
  const roles = useAppSelector((state) => state.auth.user?.roles);
  const hasSelfService = useHasSelfService();
  const isDepartmentHead = useIsDepartmentHead();
  const hasDirectReports = useHasDirectReports();

  return useMemo(
    () => ({
      permissions: permissions ?? NO_PERMISSIONS,
      roles: roles ?? NO_ROLES,
      hasSelfService,
      isDepartmentHead,
      hasDirectReports,
    }),
    [permissions, roles, hasSelfService, isDepartmentHead, hasDirectReports],
  );
}
